import { Controller } from "@hotwired/stimulus"
import {
  createChart,
  createTextWatermark,
  createSeriesMarkers,
  CandlestickSeries,
  LineSeries,
  AreaSeries,
  BarSeries,
  HistogramSeries,
  ColorType
} from 'lightweight-charts'
import {
  CHART_COLORS,
  calculateEMA,
  calculateSMA,
  buildVolumeData,
  buildWatermarkLines,
  buildPriceSeriesStyleOptions,
  buildVolumeSeriesOptions,
  buildVolumeScaleOptions,
  buildMaSeriesOptions,
  buildPriceFormat,
  DEFAULT_PRICE_PRECISION,
  buildRsLineSeriesOptions,
  buildTrendGaugeBands,
  latestTrendGaugeColor,
  contrastTextColor,
  getDefaultMaColor,
  parseMaOverlayKey,
  RS_LINE_COLOR,
  RS_LINE_DOT_COLOR,
  RS_LINE_PRICE_SCALE_ID,
  TREND_GAUGE_POSITIVE_COLOR
} from '../utils/chart_utils'
import { PowerTrendBackground } from '../utils/power_trend_background'
import { TrendGaugeBand } from '../utils/trend_gauge_band'
import { ChartMarksLayer } from '../utils/chart_marks_overlay'
import { initialLogicalRange, firstMarkIndex } from '../utils/model_book_framing'

// Stock-list keyboard navigation. The vim aliases (j/k to move, h to toggle)
// are matched on event.key rather than event.code so they follow the user's
// layout — a Dvorak "j" is KeyC — while Space/arrows stay on event.code,
// which is what makes them layout-independent. Uppercase is accepted for the
// same reason X has always been: shift shouldn't silently swallow the key.
const NEXT_ROW_LETTERS = [ 'j', 'J' ]
const PREVIOUS_ROW_LETTERS = [ 'k', 'K' ]
const TOGGLE_LETTERS = [ 'x', 'X', 'h', 'H' ]

// A bare letter is ours; the same letter under a modifier belongs to the
// browser. Ctrl/Cmd+J, +K and +H are Downloads, the omnibox and History —
// claiming those would swallow the shortcut AND, for H, perform a write by
// toggling the highlighted stock into the watchlist. Shift is deliberately
// allowed: it is what produces the accepted uppercase forms.
const hasCommandModifier = event => event.ctrlKey || event.metaKey || event.altKey

const isNextRowKey = event =>
  !hasCommandModifier(event) &&
  (event.code === 'Space' || event.code === 'ArrowDown' || NEXT_ROW_LETTERS.includes(event.key))
const isPreviousRowKey = event =>
  !hasCommandModifier(event) &&
  (event.code === 'ArrowUp' || PREVIOUS_ROW_LETTERS.includes(event.key))
const isNavigationKey = event => isNextRowKey(event) || isPreviousRowKey(event)
const isWatchlistToggleKey = event =>
  !hasCommandModifier(event) && (event.key === 'Enter' || TOGGLE_LETTERS.includes(event.key))

// Trend Gauges (Pressure / Buy Risk / TPR) — our best-effort reconstruction
// of indicators publicly associated with Mark Minervini's TradingView tools
// (Pro-only; see docs/features/TREND_GAUGES.md). Each gets its own thin
// sub-pane below the price+volume pane, in this order.
const TREND_GAUGE_DEFS = [
  { kind: 'pressure_state', label: 'Pressure' },
  { kind: 'buy_risk_state', label: 'Buy Risk' },
  { kind: 'tpr', label: 'TPR' }
]
// lightweight-charts' 30px minimum pane height only applies to setHeight;
// the stretch-factor layout we use (see applyTrendGaugePaneHeights) clamps at
// 2px, so these can go genuinely thin. The real floor is the gauge-name badge
// in the price gutter — fontSize + 2 × 2.5px padding ≈ 17px at our 12px font —
// below which it clips or bleeds into the neighbouring pane.
const TREND_GAUGE_PANE_HEIGHT = 20
// Fallback badge background when a gauge has no known state yet — neutral so
// it doesn't read as a real traffic-light color.
const TREND_GAUGE_LABEL_COLOR = '#6b7280'
// The anchor series feeds this constant for every bar instead of real
// closes: with autoscaleInfoProvider pinning the pane's range to ±1 around
// it, the "last value" badge (which carries the gauge's name — see
// ensureTrendGaugePanes) always sits vertically centered in the pane rather
// than wherever the last close happened to land. Deliberately not a round
// number so no auto-generated gridline tick can ever equal it — the
// formatter blanks every value except this one, and a tick that matched
// would show the gauge's name a second time.
const TREND_GAUGE_ANCHOR_VALUE = 0.437

// Setup Bars (Pro-only entry-signal markers, see docs/features/SETUP_BARS.md).
// A signal that fired at a rising EMA gets the stronger colour — that's the
// context that separates a pullback entry from a random narrow bar.
const SETUP_BAR_COLOR = '#94a3b8'
const SETUP_BAR_AT_MA_COLOR = '#22c55e'
const SETUP_BAR_MAX_CODES = 2

// The member's own fills, on the portfolio holdings chart (trade journal
// step 2). thinkorswim's convention, which is the cleanest known answer to
// "many fills, one candle": buys BELOW the bar, sells ABOVE, a day's fills
// stacked into one marker by the server. Prices are shown in dollars to
// three decimals — an ASX small cap trades in tenths of a cent, and rounding
// a 0.0125 fill to "0.01" on the member's own trade record is not an option.
const EXECUTION_PRICE_DECIMALS = 3

// Bars of lead-in before the first execution when the chart opens. Enough to
// show the setup the member bought into; not so much that the entry is a
// speck on the right.
const EXECUTION_RANGE_LEAD_BARS = 20

// The chart's own right-hand padding, in bars: the gap the time scale keeps
// between the last bar and the price scale so a marker on that bar is not
// clipped. It is the `rightOffset` the chart is created with, and any framing
// that calls scrollToRealTime() has to account for it — the scroll shifts the
// whole window right by this many bars WITHOUT changing barSpacing, so the
// left edge moves right by the same amount.
const CHART_RIGHT_OFFSET_BARS = 7

// Moving averages supplied by the server (a payload's `ma_overlay`) win over
// the user's configured ones for the chart that sends them. They are the
// PERSISTED MovingAverage rows an indicator's own rules were evaluated
// against — on /indexes, the 21 EMA / 50 SMA pair driving Power Trend
// starts and ends — so the lines can never disagree with the shading and
// markers drawn beside them. Recomputing those client-side would reseed the
// EMA chain from the visible window (see the EMA-chain rules in CLAUDE.md)
// and could draw a crossover the server never saw.
const SERVER_MA_LINE_WIDTH = 1.5

// The price pane's right scale margins: price normally spans the top of the
// pane down to the volume histogram (top 0.8). Margins are PRICE SCALE
// options in lightweight-charts — a `scaleMargins` on the series options is
// silently ignored — so these are the only numbers that decide where the
// candles sit.
const PRICE_SCALE_MARGIN_TOP = 0.1
const PRICE_SCALE_MARGIN_BOTTOM = 0.2
const PRICE_SCALE_MARGIN_BOTTOM_NO_VOLUME = 0.1

// The IBD-style RS line — the stock's price relative to a benchmark index,
// drawn on the price pane on its own hidden overlay scale. While it is
// drawn, the price scale's bottom margin is pushed up so the candles stop
// above the band (0.62–0.78 with volume below it, 0.62–0.95 without) and the
// line never crosses them; clearing the line restores the normal margins.
const RS_LINE_SCALE_MARGIN_TOP = 0.62
const RS_LINE_SCALE_MARGIN_BOTTOM = 0.22
const RS_LINE_SCALE_MARGIN_BOTTOM_NO_VOLUME = 0.05
const PRICE_SCALE_MARGIN_BOTTOM_WITH_RS_LINE = 0.4

// Dots on the RS line mark the days it made a new high over the server's
// lookback window. The darker, larger dot is the one that matters: RS made a
// new high while PRICE had not yet — the classic "RS line leading price"
// tell ahead of a breakout. Sizes are lightweight-charts multipliers of its
// default dot; 1 already reads as heavy on a thin line.
const RS_LINE_LEAD_MARKER_COLOR = '#1d4ed8'
const RS_LINE_LEAD_MARKER_KIND = 'rs_new_high_before_price'
const RS_LINE_MARKER_SIZE = 0.6
const RS_LINE_LEAD_MARKER_SIZE = 1.1

export default class extends Controller {
  static targets = ["chart", "tableContainer", "gaugePills", "gaugePill", "gaugeHeader"]
  static values = {
    data: Array,
    chartType: { type: String, default: 'candle' },
    upColor: { type: String, default: '' },    // Custom up-candle/bar/volume color ('' = theme default)
    downColor: { type: String, default: '' },  // Custom down-candle/bar/volume color ('' = theme default)
    width: { type: Number, default: 600 },
    height: { type: Number, default: 400 },
    timeFormat: { type: String, default: 'yyyy-MM-dd' },
    ticker: String,
    showVolume: { type: Boolean, default: true }, // Enable volume display by default
    logScale: { type: Boolean, default: false },  // Logarithmic scale toggle
    showTrendGauges: { type: Boolean, default: true }, // Pressure/Buy Risk/TPR strips + header pills (Pro payloads only)
    showSetupBars: { type: Boolean, default: true },   // Entry-bar markers (Pro payloads only); views that honour the user setting pass it explicitly
    showRsLine: { type: Boolean, default: true },      // RS (relative strength vs benchmark) overlay line
    showRsLineDots: { type: Boolean, default: true },  // New-RS-high dots on that line
    maConfig: { type: Array, default: [] },       // Moving averages configuration
    fontSize: { type: Number, default: 12 },      // Chart layout font size (axis + marker labels)
    chartDataUrl: String                          // Endpoint the view wants ticker data fetched from
  }

  // Escape HTML special characters to prevent XSS
  escapeHtml(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }

  // Find the scrollable .table-responsive container (child or ancestor of tableContainerTarget)
  get tableScrollContainer() {
    if (!this.hasTableContainerTarget) return null
    return this.tableContainerTarget.querySelector('.table-responsive') ||
           this.tableContainerTarget.closest('.table-responsive')
  }

  // Find all .chart-info elements scoped to .stock-chart-container
  get chartInfoElements() {
    return document.querySelectorAll('.stock-chart-container .chart-info')
  }

  // Show or hide the loading indicator overlay
  toggleLoadingIndicator(visible) {
    const indicator = this.chartTarget.querySelector('.chart-loading-indicator')
    if (indicator) indicator.style.display = visible ? 'block' : 'none'
  }

  connect() {
    if (!this.hasChartTarget) {
      return
    }

    // Initialize the chart after a brief delay to allow flexbox to compute
    // heights. In a hidden tab (background-tab load) the browser suspends
    // rAF callbacks entirely, so this fires arbitrarily late — on first
    // focus rather than at load. Two guards for that case:
    // - Initial setup only: by focus time tickerValueChanged has usually
    //   already built AND populated the chart; rebuilding here would wipe
    //   it back to the empty dataValue ([]) with nothing left to refetch,
    //   leaving a permanently blank chart.
    // - Replay a bailed fetch: the 300ms fetch below can run before any
    //   chart exists (rAF suspended) and bail on !this.series — it parks
    //   its ticker in pendingFetchTicker for this callback to pick up.
    requestAnimationFrame(() => {
      if (!this.chart) this.setupChart()
      if (this.pendingFetchTicker && this.series) {
        const ticker = this.pendingFetchTicker
        this.pendingFetchTicker = null
        this.fetchDataForTicker(ticker)
      }
    })

    // Re-apply theme-aware colours when the user toggles light/dark.
    this.boundThemeChangedHandler = () => this.applyChartTheme()
    document.addEventListener('theme:changed', this.boundThemeChangedHandler)

    // Add keyboard event listener for navigation
    this.addKeyboardNavigation()

    // After a short delay to ensure the chart is ready...
    setTimeout(() => {
      // If we have a ticker, fetch data for that
      if (this.hasTickerValue && this.tickerValue) {
        // Using initial ticker value
        this.fetchDataForTicker(this.tickerValue)

        // Also highlight the corresponding row in the table
        if (this.hasTableContainerTarget) {
          const row = this.tableContainerTarget.querySelector(`tr[data-stock-ticker="${this.tickerValue}"]`)
          if (row) {
            row.classList.add('active')
            this.currentRow = row
          }
        }
      }
      // Otherwise, if we have a table with rows, use the first item's ticker
      else if (this.hasTableContainerTarget) {
        const firstRow = this.tableContainerTarget.querySelector('tbody tr')
        if (firstRow) {
          const firstTicker = firstRow.dataset.stockTicker
          if (firstTicker) {
            // Using first row ticker
            this.tickerValue = firstTicker
            firstRow.classList.add('active')
            this.currentRow = firstRow
            // tickerValueChanged will be called automatically

            // Pin the list to the very top. While the chart + table lay out
            // asynchronously (rAF chart setup, flex height, this deferred
            // highlight), the browser can scroll-anchor a fractional scrollTop
            // onto the container, sliding the first row up under the sticky
            // header so its top half is clipped. Resetting to 0 fixes it.
            const scrollContainer = this.tableScrollContainer
            if (scrollContainer) scrollContainer.scrollTop = 0
          }
        }
      }
    }, 300)
  }

  // Add keyboard navigation support
  addKeyboardNavigation() {
    if (!this.hasTableContainerTarget) return

    // Add focus support for table rows
    this.tableContainerTarget.querySelectorAll('tbody tr').forEach(row => {
      row.setAttribute('tabindex', '0')
    })

    // Bind the event handlers once and store them
    this.boundGlobalKeydownHandler = this.globalKeydownHandler.bind(this)

    // Add keyboard listener to the document for navigation
    document.addEventListener('keydown', this.boundGlobalKeydownHandler)
  }

  // Handle keyboard navigation events
  handleKeyDown(event) {
    // Only handle the navigation keys (space / arrows / vim j and k)
    if (!isNavigationKey(event)) return

    // Prevent default behavior (scrolling)
    event.preventDefault()

    if (!this.hasTableContainerTarget) return

    const rows = this.tableContainerTarget.querySelectorAll('tbody tr')
    if (!rows.length) return

    // If no current row is selected, select the first one
    if (!this.currentRow) {
      this.currentRow = rows[0]
      this.selectRow(this.currentRow)
      return
    }

    // Find the index of the current row
    const currentIndex = Array.from(rows).indexOf(this.currentRow)

    let nextIndex = currentIndex;

    // Determine next index based on key pressed
    if (isNextRowKey(event)) {
      // Move to next row (if not at the last row already)
      if (currentIndex < rows.length - 1) {
        nextIndex = currentIndex + 1
      } else {
        // At last row - don't loop around to the first row
        // Instead try to trigger infinite scroll if we're near the bottom
        const scrollContainer = this.tableScrollContainer
        if (scrollContainer) {
          // Force scroll to bottom to attempt to trigger infinite scroll
          scrollContainer.scrollTop = scrollContainer.scrollHeight

          // Wait a bit and check if new rows were loaded
          setTimeout(() => {
            const updatedRows = this.tableContainerTarget.querySelectorAll('tbody tr')

            // If we have more rows after waiting, select the next one
            if (updatedRows.length > rows.length) {
              this.selectRow(updatedRows[currentIndex + 1])
            }
          }, 500)
        }
        return // Don't try to select a new row yet
      }
    } else if (isPreviousRowKey(event)) {
      // Move to previous row (if not at the first row)
      if (currentIndex > 0) {
        nextIndex = currentIndex - 1
      } else {
        return // At first row, nowhere to go
      }
    }

    const nextRow = rows[nextIndex]

    // Select the next row and ensure it's visible
    if (nextRow) {
      this.selectRow(nextRow)
      this.scrollRowIntoView(nextRow)
    }
  }

  // Enhanced version of keyboard handling for document-level keydown events
  // This allows keyboard navigation to work even if the table doesn't have focus
  globalKeydownHandler(event) {
    // Only process keyboard events if the page is not in an input field
    if (event.target.tagName === 'INPUT' ||
        event.target.tagName === 'TEXTAREA' ||
        event.target.tagName === 'SELECT' ||
        event.target.isContentEditable) {
      return
    }

    // Delegate to regular handler for our specific keys
    if (isNavigationKey(event)) {
      this.handleKeyDown(event)
    }

    // Handle X / H or Enter key for watchlist toggle
    if (isWatchlistToggleKey(event)) {
      this.dispatchWatchlistToggle()
    }
  }

  // Dispatch watchlist toggle event for the current row
  dispatchWatchlistToggle() {
    if (!this.currentRow) return

    const ticker = this.currentRow.dataset.stockTicker
    if (!ticker) return

    // Dispatch custom event that watchlist-selection controller listens for
    document.dispatchEvent(new CustomEvent('watchlist:toggle', {
      detail: { ticker },
      bubbles: true
    }))
  }

  // Simple method to scroll a row into view
  scrollRowIntoView(row) {
    const container = this.tableScrollContainer
    if (!container || !row) return

    const containerRect = container.getBoundingClientRect()
    const rowRect = row.getBoundingClientRect()

    if (rowRect.bottom > containerRect.bottom) {
      container.scrollTop += (rowRect.bottom - containerRect.bottom + 20)
    } else if (rowRect.top < containerRect.top) {
      container.scrollTop -= (containerRect.top - rowRect.top + 20)
    }
  }

  // Select a row programmatically
  selectRow(row) {
    if (!row) return

    const ticker = row.dataset.stockTicker
    if (!ticker) return

    // Update current row reference
    this.currentRow = row

    // Remove active class from all rows
    this.tableContainerTarget.querySelectorAll('tr').forEach(r => {
      r.classList.remove('active')
    })

    // Add active class to the selected row
    row.classList.add('active')

    // Update the chart with the new ticker
    if (this.hasChartTarget && this.series) {
      this.fetchDataForTicker(ticker)
    }

    // Update the ticker value
    this.tickerValue = ticker
  }

  disconnect() {
    // Clean up - the chart.remove() method will automatically remove all series
    // (but never calls a primitive's detached(), so the marks layer goes first)
    if (this.chart) {
      this.disposeMarksLayer()
      this.chart.remove()
      this.chart = null
      this.series = null
      this.volumeSeries = null
      this.maLines = []
      this.powerTrendMarkers = null
      this.setupBarMarkers = null
      this.executionMarkers = null
      this.rsLineSeries = null
      this.rsLineMarkers = null
      this.rsLineLabel = null
      this.powerTrendBackground = null
      this.autoBtn = null
      this.logBtn = null
      this.trendGaugePanes = null
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    // Remove keyboard event listener
    if (this.boundGlobalKeydownHandler) {
      document.removeEventListener('keydown', this.boundGlobalKeydownHandler)
      this.boundGlobalKeydownHandler = null
    }

    if (this.boundThemeChangedHandler) {
      document.removeEventListener('theme:changed', this.boundThemeChangedHandler)
      this.boundThemeChangedHandler = null
    }
  }

  // Refresh layout/grid/scale/crosshair colours from the live CSS variables.
  // Called on theme:changed; chart instance survives, no series rebuild needed.
  applyChartTheme() {
    if (!this.chart) return
    try {
      this.chart.applyOptions({
        layout: {
          background: { type: ColorType.Solid, color: CHART_COLORS.background },
          textColor: CHART_COLORS.text
        },
        grid: {
          vertLines: { color: CHART_COLORS.grid },
          horzLines: { color: CHART_COLORS.grid }
        },
        timeScale: { borderColor: CHART_COLORS.border },
        rightPriceScale: { borderColor: CHART_COLORS.border },
        crosshair: {
          vertLine: { color: CHART_COLORS.crosshair },
          horzLine: { color: CHART_COLORS.crosshair }
        }
      })
    } catch (e) {
      console.warn('Failed to re-apply lightweight-chart theme:', e)
    }
  }

  setupChart() {
    // Every rebuild is a new generation: a fetch that started against the
    // old chart checks this after its awaits and drops its response instead
    // of calling setData on a series that died with the old instance.
    this.chartGeneration = (this.chartGeneration || 0) + 1

    // Clear existing chart
    if (this.chart) {
      // chart.remove() never calls a primitive's detached(), so the marks
      // layer is disposed while its series still exist; the next setMarks
      // builds a fresh one on the new series.
      this.disposeMarksLayer()
      this.chart.remove()
      this.chart = null
      // A removed chart takes every pane/series with it — without this, a
      // later fetch's ensureTrendGaugePanes() sees a stale non-null
      // trendGaugePanes (from the old chart instance) and skips recreating
      // the panes on the new one, leaving the gauges silently missing.
      this.trendGaugePanes = null
      // Same reasoning for the marker primitives: they died with the chart, so
      // holding the handles would make the next detach() throw on a disposed
      // object instead of simply recreating them.
      this.powerTrendMarkers = null
      this.setupBarMarkers = null
      this.executionMarkers = null
      // Same again for the RS overlay: its series died with the chart, and a
      // stale handle would have the next clearRsLine() call removeSeries() on
      // a disposed object instead of simply drawing a fresh line.
      this.rsLineSeries = null
      this.rsLineMarkers = null
      this.rsLineLabel = null
    }

    // Make sure the target element is ready for the chart
    const containerWidth = this.chartTarget.clientWidth || this.widthValue

    // Use container's computed height if available, fall back to value or default
    // Don't set explicit pixel height - let CSS/flexbox handle sizing
    let containerHeight = this.chartTarget.clientHeight
    if (containerHeight < 100) {
      // Container height not yet computed by flexbox, use fallback
      containerHeight = this.heightValue || 400
    }

    // Clear any previous content
    this.chartTarget.innerHTML = ''

    // Chart initialization with explicit dimensions
    const chartOptions = this.buildInteractiveChartOptions(containerWidth, containerHeight)

    try {
      // Create the chart
      this.chart = createChart(this.chartTarget, chartOptions)

      // Resize handling
      if (this.resizeObserver) {
        this.resizeObserver.disconnect()
      }

      this.resizeObserver = new ResizeObserver(entries => {
        if (entries.length === 0 || entries[0].target !== this.chartTarget) return

        const newRect = entries[0].contentRect
        if (newRect.width > 0 && newRect.height > 0 && this.chart) {
          this.chart.applyOptions({
            width: newRect.width,
            height: newRect.height
          })
          // Pane heights are stretch factors internally, so the thin gauge
          // strips scale with every total-height change — re-pin them.
          this.applyTrendGaugePaneHeights()
        }
      })

      this.resizeObserver.observe(this.chartTarget)

      // Add data series based on type
      this.addSeries()

      // Add scale toggle buttons (A = Auto, L = Log)
      this.createScaleButtons()
    } catch (err) {
      this.chartTarget.innerHTML = `<div class="chart-error">Error creating chart: ${this.escapeHtml(err.message)}</div>`
    }
  }

  // Build chart options for interactive charts (with scroll, scale, crosshair enabled)
  buildInteractiveChartOptions(width, height) {
    return {
      width,
      height,
      layout: {
        background: { type: ColorType.Solid, color: CHART_COLORS.background },
        textColor: CHART_COLORS.text,
        fontSize: this.fontSizeValue,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif',
      },
      grid: {
        vertLines: { color: CHART_COLORS.grid },
        horzLines: { color: CHART_COLORS.grid },
      },
      timeScale: {
        timeVisible: false,
        secondsVisible: false,
        borderColor: CHART_COLORS.border,
        rightOffset: CHART_RIGHT_OFFSET_BARS,
        barSpacing: 10,
        minBarSpacing: 2,
        fixLeftEdge: true,
        lockVisibleTimeRangeOnResize: true,
        localization: {
          locale: 'en-US',
          dateFormat: "dd MMM 'yy",
        },
      },
      rightPriceScale: {
        borderColor: CHART_COLORS.border,
        visible: true,
        borderVisible: true,
        autoScale: true,
        mode: this.getLogScaleSetting() ? 1 : 0,
        scaleMargins: this.priceScaleMargins(),
      },
      crosshair: {
        mode: 1,
        vertLine: {
          width: 1,
          color: CHART_COLORS.crosshair,
          style: 3,
        },
        horzLine: {
          width: 1,
          color: CHART_COLORS.crosshair,
          style: 3,
        },
      },
      handleScroll: {
        vertTouchDrag: true,
        pressedMouseMove: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true,
      },
      kineticScroll: {
        mouse: true,
        touch: true,
      }
    }
  }

  // Create A (Auto) and L (Log) scale toggle buttons
  createScaleButtons() {
    // Remove existing buttons if any
    const existingButtons = this.chartTarget.querySelector('.chart-scale-buttons')
    if (existingButtons) {
      existingButtons.remove()
    }

    // Create container for buttons
    const container = document.createElement('div')
    container.className = 'chart-scale-buttons'

    const isLogScale = this.getLogScaleSetting()
    // Store the initial value for setScaleMode comparisons
    this.logScaleValue = isLogScale

    // Auto scale button
    const autoBtn = document.createElement('button')
    autoBtn.className = 'scale-btn'
    autoBtn.textContent = 'A'
    autoBtn.title = 'Auto scale'
    autoBtn.classList.toggle('active', !isLogScale)
    autoBtn.addEventListener('click', () => this.setScaleMode(false))

    // Log scale button
    const logBtn = document.createElement('button')
    logBtn.className = 'scale-btn'
    logBtn.textContent = 'L'
    logBtn.title = 'Logarithmic scale'
    logBtn.classList.toggle('active', isLogScale)
    logBtn.addEventListener('click', () => this.setScaleMode(true))

    container.appendChild(autoBtn)
    container.appendChild(logBtn)
    this.chartTarget.appendChild(container)

    // Store references for updating
    this.autoBtn = autoBtn
    this.logBtn = logBtn
  }

  // Toggle between linear and logarithmic scale
  setScaleMode(useLog) {
    if (this.logScaleValue === useLog) return

    this.logScaleValue = useLog

    // Update button states
    if (this.autoBtn && this.logBtn) {
      this.autoBtn.classList.toggle('active', !useLog)
      this.logBtn.classList.toggle('active', useLog)
    }

    // Update the chart's price scale
    if (this.chart) {
      this.chart.priceScale('right').applyOptions({
        mode: useLog ? 1 : 0
      })
    }
  }

  // Get the chart style (type + up/down colors) from data attributes.
  // Like log scale and MA config, the attributes may live on the
  // .stock-chart-container descendant rather than the controller element —
  // most pages declare the controller on an outer wrapper and render the
  // shared stock_chart partial inside it.
  getChartStyle() {
    let type = this.chartTypeValue
    let up = this.upColorValue
    let down = this.downColorValue

    try {
      if (this.element && !this.element.hasAttribute('data-lightweight-chart-chart-type-value')) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          type = container.dataset.lightweightChartChartTypeValue || type
          up = container.dataset.lightweightChartUpColorValue || up
          down = container.dataset.lightweightChartDownColorValue || down
        }
      }
    } catch (e) {
      // Controller not fully initialized
    }

    return { type: type || 'candle', up: up || null, down: down || null }
  }

  // Line/area series take {time, value} points; candle/bar take the raw OHLC
  // items as-is. Everything downstream (volume, MAs, markers) keys on time,
  // so only the main series data needs the mapping.
  priceSeriesData(data) {
    const { type } = this.getChartStyle()
    if (type === 'line' || type === 'area') {
      return data.map(item => ({ time: item.time, value: item.close }))
    }
    return data
  }

  // Decimal places the price axis renders at. Defaults to the app-wide two
  // (the ASX answer) and is replaced per security from the chart payload's
  // stock.price_scale — see applyPricePrecision.
  get pricePrecision() {
    return this._pricePrecision ?? DEFAULT_PRICE_PRECISION
  }

  // Re-format the price series and every MA line already on the chart. A
  // no-op when the precision hasn't changed, so switching between two ASX
  // tickers costs nothing.
  applyPricePrecision(scale) {
    const next = Number.isFinite(Number(scale)) ? Number(scale) : DEFAULT_PRICE_PRECISION
    if (next === this.pricePrecision) return

    this._pricePrecision = next
    const format = buildPriceFormat(next)
    try {
      this.series?.applyOptions({ priceFormat: format })
      this.maLines?.forEach(({ series }) => series?.applyOptions({ priceFormat: format }))
    } catch {
      /* a mis-formatted axis beats a blank chart */
    }
  }

  addSeries() {
    if (!this.chart) return

    const mainChartOptions = { priceScaleId: 'right' }

    const seriesClasses = {
      candle: CandlestickSeries,
      line:   LineSeries,
      area:   AreaSeries,
      bar:    BarSeries,
    }

    const { type, up, down } = this.getChartStyle()
    const SeriesType = seriesClasses[type] || seriesClasses.candle
    const typeOptions = buildPriceSeriesStyleOptions(type, up, down)
    this.series = this.chart.addSeries(SeriesType, {
      ...typeOptions,
      ...mainChartOptions,
      priceFormat: buildPriceFormat(this.pricePrecision),
    })

    // Create the volume series if volume chart is enabled
    if (this.showVolumeValue) {
      try {
        this.volumeSeries = this.chart.addSeries(HistogramSeries, buildVolumeSeriesOptions(up))
        this.chart.priceScale('volume').applyOptions(buildVolumeScaleOptions())
      } catch (err) {
        this.volumeSeries = null;
      }
    }

    // Set the data if we have it
    if (this.hasDataValue && this.dataValue.length > 0) {
      this.series.setData(this.priceSeriesData(this.dataValue))

      // Set volume data if available
      if (this.showVolumeValue && this.volumeSeries) {
        const { up, down } = this.getChartStyle()
        this.volumeSeries.setData(buildVolumeData(this.dataValue, up, down))
      }

      this.setDefaultVisibleRange(this.dataValue)
    }
  }

  // Handle ticker selection from the table
  selectTicker(event) {
    // If the click originated on a link inside the row (e.g. the ticker
    // anchor that navigates to /stocks/:ticker), let the browser handle
    // the navigation instead of swallowing the click.
    if (event.target.closest('a')) return

    event.preventDefault()

    // Select the row that was clicked
    this.selectRow(event.currentTarget)

    // Also ensure the row is visible
    this.scrollRowIntoView(event.currentTarget)

    const ticker = event.currentTarget.dataset.stockTicker
    if (ticker) {
      this.chartInfoElements.forEach(el => {
        el.textContent = ticker
      })

      if (this.hasChartTarget && !this.series) {
        this.setupChart()
      }
    }
  }

  tickerValueChanged() {
    // This is only used for the initial loading - we handle user clicks directly in selectTicker
    // Only proceed if this wasn't triggered by a user click (which we already handled)
    if (document.readyState !== 'complete') {
      return
    }

    // Only handle unprocessed ticker changes (like from the backend)
    if (!this._lastProcessedTicker || this._lastProcessedTicker !== this.tickerValue) {
      this._lastProcessedTicker = this.tickerValue

      if (!this.tickerValue) return

      // Update chart info (only in stock chart container)
      this.chartInfoElements.forEach(el => {
        el.textContent = this.tickerValue
      })

      // Only proceed if we have a chart
      if (this.hasChartTarget) {
        // Make sure chart is initialized
        if (!this.chart || !this.series) {
          this.setupChart()
        }

        // Fetch data if we have a ticker and the chart is ready
        if (this.tickerValue && this.series) {
          this.fetchDataForTicker(this.tickerValue)
        }
      }
    }
  }

  // Legacy fallback only — views should declare the endpoint explicitly via
  // data-lightweight-chart-chart-data-url-value. Path sniffing is unreliable
  // (e.g. /stocks matches nothing and lands on the tier-gated IPO endpoint).
  getEndpointForPath(pathname) {
    if (pathname.includes('metrics') || pathname.includes('relative-strength')) {
      return '/metrics/chart_data'
    }
    if (pathname.includes('indexes')) {
      return '/indexes/chart_data'
    }
    return '/ipos/chart_data'
  }

  async fetchDataForTicker(ticker) {
    if (!ticker) return
    if (!this.series) {
      // No chart yet (hidden-tab load: the rAF that builds it is suspended
      // until the tab is focused) — park the ticker so connect()'s rAF
      // callback can replay this fetch once the chart exists.
      this.pendingFetchTicker = ticker
      return
    }
    this.pendingFetchTicker = null

    this.toggleLoadingIndicator(true)
    this.clearChartError()

    const endpoint = this.chartDataUrlValue || this.getEndpointForPath(window.location.pathname)

    try {
      // Ask for JSON explicitly. Without it Rails answers these endpoints as
      // HTML, and a controller wrapped in PortfolioFailureHandling answers a
      // failure with a 302 to a page that works — which `fetch` follows, so
      // the error arrives here as an HTML body that `.json()` chokes on and
      // the member is told nothing. With the header the failure is a 5xx we
      // can report. Every chart_data endpoint renders JSON unconditionally,
      // so this is safe for all of them.
      const generation = this.chartGeneration
      const httpResponse = await fetch(`${endpoint}?ticker=${ticker}`, {
        headers: { Accept: 'application/json' }
      })
      if (!httpResponse.ok) {
        throw new Error(`HTTP error! Status: ${httpResponse.status}`)
      }

      const response = await httpResponse.json()
      // The chart was rebuilt (or removed) while this request was in flight:
      // the series this fetch would write to no longer exists.
      if (generation !== this.chartGeneration || !this.series) {
        this.toggleLoadingIndicator(false)
        return
      }
      this.toggleLoadingIndicator(false)

      const { data, stock: stockInfo, ma_overlay: maOverlay, power_trend_periods: powerTrendPeriods, trend_gauges: trendGauges, setup_bars: setupBars, executions, rs_line: rsLine, marks } = response

      if (!data || data.length === 0) {
        this.showChartError(`No chart data available for ${ticker}`)
        return
      }

      // Each unguarded render step is named in the error it raises: a
      // lightweight-charts assertion reads "Value is null" whatever threw it,
      // and the console stack hides the library's frames by default.
      const step = (name, fn) => {
        try {
          fn()
        } catch (err) {
          if (err && typeof err.message === 'string') err.message = `${name}: ${err.message}`
          throw err
        }
      }

      step('watermark', () => this.updateWatermark(stockInfo))
      // Before setData: the axis is built from the first points it receives,
      // so a late precision change leaves the first render rounded.
      step('price precision', () => this.applyPricePrecision(stockInfo?.price_scale))

      const days = data.length
      const firstDate = data[0].time
      const lastDate = data[data.length - 1].time

      this.chartInfoElements.forEach(el => {
        el.innerHTML = `${this.escapeHtml(ticker)} <span class="text-muted small">(${this.escapeHtml(String(days))} days: ${this.escapeHtml(firstDate)} to ${this.escapeHtml(lastDate)})</span>`
      })

      step('price series', () => this.series.setData(this.priceSeriesData(data)))

      if (this.showVolumeValue && this.volumeSeries) {
        const { up, down } = this.getChartStyle()
        try { this.volumeSeries.setData(buildVolumeData(data, up, down)) } catch { /* continue without volume */ }
      }

      try {
        this.clearMovingAverageLines()
        if (!this.addServerMovingAverages(maOverlay)) {
          this.addConfiguredMovingAverages(data)
        }
      } catch {
        this.maLines = []
      }

      try {
        this.updateRsLine(rsLine)
      } catch {
        /* a missing overlay beats a missing chart */
      }

      step('power trend', () => {
        if (powerTrendPeriods?.length > 0) {
          this.addPowerTrendMarkers(powerTrendPeriods)
          this.addPowerTrendBackground(powerTrendPeriods, data)
        } else {
          this.removePowerTrendBackground()
        }
      })
      this.retriedTicker = null

      try {
        this.updateTrendGauges(trendGauges, data)
      } catch {
        /* a broken trend-gauge render should never blank the whole chart */
      }

      try {
        // The executions go in too: a buy and a setup bar on the same candle
        // collide, and the buy wins (see addSetupBarMarkers).
        this.addSetupBarMarkers(setupBars, executions)
      } catch {
        /* likewise: missing markers beat a missing chart */
      }

      try {
        this.addExecutionMarkers(executions)
      } catch {
        /* likewise: missing markers beat a missing chart */
      }

      // Curated Model Book marks: only that endpoint sends the key (an array,
      // possibly empty). Every other chart carries none and builds nothing.
      const hasMarks = Array.isArray(marks)
      try {
        if (hasMarks) {
          this.setMarks(marks)
          // "lightweight-chart:marks-loaded": the chart, its series and the
          // marks layer now exist — the cue for model-book-chart and the
          // drawing editor to bind to this controller.
          this.dispatch('marks-loaded', { detail: { marks, modelBook: response.model_book } })
        } else if (this.marksLayer) {
          this.marksLayer.setMarks([])
        }
      } catch (err) {
        console.error('[lightweight-chart] marks failed to draw', err)
      }

      setTimeout(() => {
        if (generation !== this.chartGeneration) return
        if (this.chart && typeof this.chart.timeScale === 'function') {
          // A Model Book chart's bars ARE its window (marks ± context): it
          // opens on all of them, or on its first mark when they won't fit
          // readably (frameMarksWindow). A chart carrying the member's
          // executions opens on the executions; every other chart on the
          // default last year.
          if (hasMarks) {
            this.frameMarksWindow(data)
          } else if (!this.setVisibleRangeCoveringExecutions(executions, data)) {
            this.setDefaultVisibleRange(data)
          }
        }
      }, 100)
    } catch (error) {
      // Keep the stack in the console: the overlay only shows the message,
      // and a lightweight-charts assertion is meaningless without its frames.
      console.error(`[lightweight-chart] load failed for ${ticker}`, error)
      this.handleFetchError(ticker, error)
    }
  }

  handleFetchError(ticker, error) {
    this.toggleLoadingIndicator(false)
    // A failed load must never take the chart with it. This used to replace
    // the container's HTML with the message while this.chart / this.series
    // still pointed at the now-detached canvas, so every later fetch drew
    // into a dead chart and lightweight-charts threw "Value is null" for
    // every stock after the first bad one — including ones that had just
    // rendered fine (production, 2026-09-28). An HTTP failure leaves the
    // chart healthy; anything else came out of the render path, so the
    // chart is rebuilt before the message goes on top of it.
    if (!String(error?.message).startsWith('HTTP error')) {
      this.rebuildChartAfterError()
      // One retry on the fresh chart: the failure has only ever been seen as
      // a lightweight-charts assertion inside the render path, and a rebuilt
      // chart draws the same payload fine. A second failure shows the box.
      if (this.retriedTicker !== ticker) {
        this.retriedTicker = ticker
        this.fetchDataForTicker(ticker)
        return
      }
    }
    this.retriedTicker = null
    this.showChartError(`Error loading chart data for ${ticker}: ${error.message}`)
  }

  rebuildChartAfterError() {
    try {
      this.setupChart()
    } catch {
      /* setupChart reports its own failure into the container */
    }
  }

  // Overlay the message on the chart rather than replacing it.
  showChartError(message) {
    this.clearChartError()
    this.chartTarget.classList.add('error')
    const el = document.createElement('div')
    el.className = 'chart-error'
    el.textContent = message
    this.chartTarget.appendChild(el)
  }

  clearChartError() {
    this.chartTarget.classList.remove('error')
    this.chartTarget.querySelectorAll('.chart-error').forEach(el => el.remove())
  }

  dataValueChanged() {
    // Reinitialize the chart when data changes
    if (this.chart) {
      this.setupChart()
    }
  }

  // Clear all existing moving average lines
  clearMovingAverageLines() {
    if (!this.maLines?.length) return

    this.maLines.forEach(({ series }) => {
      if (series) this.chart.removeSeries(series)
    })
    this.maLines = []
    // The labels belong to the lines just removed; without this a fetch
    // whose MA block throws would re-render the previous ticker's labels
    // beside the RS label.
    this.maLabels = []
  }

  // Get log scale setting from data attribute (may be on descendant element)
  getLogScaleSetting() {
    try {
      // First try Stimulus value (if controller element has the attribute)
      if (this.element && this.element.hasAttribute('data-lightweight-chart-log-scale-value')) {
        return this.logScaleValue
      }

      // Fall back to reading from stock-chart-container element
      if (this.element) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          const value = container.dataset.lightweightChartLogScaleValue
          return value === 'true'
        }
      }
    } catch (e) {
      // Controller not fully initialized
    }

    return false
  }

  // Get MA config from data attribute (may be on descendant element)
  getMaConfig() {
    // First try Stimulus value (if controller element has the attribute)
    if (this.hasMaConfigValue && this.maConfigValue.length) {
      return this.maConfigValue
    }

    // Fall back to reading from stock-chart-container element
    try {
      if (this.element) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          const configJson = container.dataset.lightweightChartMaConfigValue
          if (configJson) {
            return JSON.parse(configJson)
          }
        }
      }
    } catch (e) {
      // Controller not fully initialized or parse error
    }

    return []
  }

  // Plot server-supplied moving averages (see SERVER_MA_LINE_WIDTH for why
  // these take precedence). Returns false when the payload carries none, so
  // the caller can fall back to the user's configured MAs.
  addServerMovingAverages(maOverlay) {
    const keys = Object.keys(maOverlay || {}).filter(key => maOverlay[key]?.length)
    if (!keys.length) return false

    const labels = []
    this.maLines = []

    // Shortest period first, so the labels read the way a trader would say
    // the pair ("21 EMA above the 50 SMA") rather than in hash order.
    keys
      .map(key => ({ key, ...parseMaOverlayKey(key) }))
      .sort((a, b) => a.period - b.period)
      .forEach(({ key, type, period }) => {
        const color = getDefaultMaColor(period)
        const series = this.chart.addSeries(LineSeries, buildMaSeriesOptions({
          color,
          lineWidth: SERVER_MA_LINE_WIDTH,
          precision: this.pricePrecision
        }))
        series.setData(maOverlay[key])

        this.maLines.push({ series, config: { type, period, color } })
        labels.push(`<span style="color: ${color}; font-weight: bold;">[${period}-day ${type}]</span>`)
      })

    this.updateIndicatorLabels(labels)
    return true
  }

  // Add moving averages based on user configuration
  addConfiguredMovingAverages(formattedData) {
    const maConfig = this.getMaConfig()
    if (!maConfig.length) return

    const labels = []
    this.maLines = []

    maConfig.forEach(config => {
      if (!config.enabled || formattedData.length < config.period) return

      const maData = config.type === 'ema'
        ? calculateEMA(formattedData, config.period)
        : calculateSMA(formattedData, config.period)

      const series = this.chart.addSeries(LineSeries, buildMaSeriesOptions({
        color: config.color,
        lineWidth: parseFloat(config.line_width) || 1.0,
        precision: this.pricePrecision
      }))
      series.setData(maData)

      this.maLines.push({ series, config })

      const bold = parseFloat(config.line_width) >= 1.5 ? 'font-weight: bold;' : ''
      labels.push(`<span style="color: ${config.color}; ${bold}">[${config.period}-day ${config.type.toUpperCase()}]</span>`)
    })

    this.updateIndicatorLabels(labels)
  }

  // Update the chart info display with indicator labels.
  //
  // This rebuilds the whole label run from the ticker onwards, so the RS
  // line's own label — added later in the fetch, and possibly with no moving
  // averages at all — is remembered separately and re-appended after the MA
  // labels every time rather than being wiped by the next MA render.
  updateIndicatorLabels(labels) {
    this.maLabels = labels
    const all = this.rsLineLabel ? [ ...labels, this.rsLineLabel ] : labels

    this.chartInfoElements.forEach(el => {
      // Get only the ticker part (text before any span)
      const tickerOnly = el.innerHTML.split('<span')[0].trim()
      // Add the indicator labels
      el.innerHTML = `${tickerOnly} ${all.join(' ')}`
    })
  }

  // Set (or clear, with null) the RS line's header label and redraw the run.
  setRsLineLabel(label) {
    this.rsLineLabel = label
    this.updateIndicatorLabels(this.maLabels || [])
  }

  // The one home for the detach → null → guard → sort → create dance that
  // every marker set on this chart needs. The three builders below only build
  // arrays; this owns the lifecycle.
  //
  // `fieldName` names the handle the primitive is parked on
  // (`powerTrendMarkers` / `setupBarMarkers` / `executionMarkers` /
  // `rsLineMarkers`), because `disconnect()` and `setupChart()` null those
  // same fields when the chart they belong to is torn down. `series` is the
  // series the markers hang off — the main price series for everything except
  // the RS dots, which belong on the RS line itself.
  //
  // Detaching is try/finally, not a bare call: after a Turbo cache restore the
  // primitive can belong to a disposed chart and throw, and an escaping error
  // would leave the field pointing at the dead primitive forever — every later
  // load would then throw here and silently render no markers at all. An empty
  // array is a legitimate call: it detaches what is drawn and draws nothing,
  // which is what "the member turned this off" has to mean.
  replaceMarkers(fieldName, markers, series = this.series) {
    if (!series) return

    if (this[fieldName]) {
      try {
        this[fieldName].detach()
      } catch {
        /* already disposed with its chart */
      } finally {
        this[fieldName] = null
      }
    }

    if (!markers?.length) return

    // Sorted by time, which lightweight-charts requires.
    markers.sort((a, b) => String(a.time).localeCompare(String(b.time)))

    try {
      this[fieldName] = createSeriesMarkers(series, markers)
    } catch {
      /* continue without these markers — a missing marker beats a missing chart */
    }
  }

  // Add PowerTrend markers to the chart
  // Shows trend start (green arrow up) and trend end (red arrow down)
  addPowerTrendMarkers(periods) {
    const markers = []
    const periodList = periods || []

    periodList.forEach(period => {
      // Add start marker (green arrow up)
      if (period.start_date) {
        markers.push({
          time: period.start_date,
          position: 'belowBar',
          color: '#22c55e', // Green
          shape: 'arrowUp',
          text: 'PT',
          size: 2
        })
      }

      // Add end marker (red/orange arrow down) if the trend has ended
      if (period.end_date) {
        const endColor = period.end_reason === 'circuit_breaker' ? '#ef4444' : '#f97316'
        const endText = period.end_reason === 'circuit_breaker' ? 'CB' : 'EMA'

        markers.push({
          time: period.end_date,
          position: 'aboveBar',
          color: endColor,
          shape: 'arrowDown',
          text: endText,
          size: 2
        })
      }
    })

    this.replaceMarkers('powerTrendMarkers', markers)
  }

  // Add Setup Bar (entry-signal) markers below the candles: NR7, inside
  // days, hammers and reversal bars, labelled with their short codes.
  //
  // The API omits setup_bars entirely for non-Pro users, so an absent
  // payload just means "no markers", not an error. Markers that fired at a
  // rising EMA are drawn in the stronger colour — that's the context that
  // makes an entry bar worth acting on.
  //
  // A setup bar and a BUY both sit `belowBar` with an `arrowUp`, so on a day
  // the member bought a setup bar the two markers collide and neither is
  // readable. The buy wins and the setup bar is dropped for that bar only:
  // this chart exists to show the member their own trades, and the signal that
  // was there anyway is the lesser fact. The setup bar stays in the payload —
  // this is a drawing decision, not a filter on the data.
  addSetupBarMarkers(setupBars, executions) {
    const enabled = this.getShowSetupBarsSetting()
    const buyDates = new Set(
      (executions || [])
        .filter(execution => execution.side === 'buy')
        .map(execution => String(execution.time))
    )

    const markers = (enabled ? (setupBars || []) : [])
      .filter(bar => !buyDates.has(String(bar.time)))
      .map(bar => ({
        time: bar.time,
        position: 'belowBar',
        color: bar.at_ma ? SETUP_BAR_AT_MA_COLOR : SETUP_BAR_COLOR,
        shape: 'arrowUp',
        // Two codes is all that stays legible under a candle; the rest are in
        // the list on /setup-bars. Separator is '/' rather than '+', because a
        // code can itself carry a strength tier suffix ("GDR+").
        text: (bar.patterns || []).slice(0, SETUP_BAR_MAX_CODES).join('/'),
        size: 1
      }))

    this.replaceMarkers('setupBarMarkers', markers)
  }

  // Draw the member's OWN executions on the bars: the portfolio holdings
  // chart's whole point (trade journal step 2). Buys below the bar with an up
  // arrow, sells above with a down arrow — thinkorswim's convention, and the
  // one every trader in this category already reads without a legend.
  //
  // The server has already stacked a day's fills into one marker per side, so
  // "B 500 @ 1.230" on a candle means the day's total and its value-weighted
  // average price. Only the holdings endpoint sends the key, so an absent
  // payload just means "not that chart", not an error.
  //
  // Colours come from getChartStyle() — the member's own up/down candle
  // colours, falling back to the theme's — so a buy marker reads as a buy in
  // light and dark and under a custom palette, without a fourth colour to keep
  // in step with the other three.
  addExecutionMarkers(executions) {
    const { up, down } = this.getChartStyle()
    const buyColor = up || CHART_COLORS.up
    const sellColor = down || CHART_COLORS.down

    const markers = (executions || []).map(execution => {
      const buy = execution.side === 'buy'
      return {
        time: execution.time,
        position: buy ? 'belowBar' : 'aboveBar',
        color: buy ? buyColor : sellColor,
        shape: buy ? 'arrowUp' : 'arrowDown',
        text: this.executionMarkerText(execution),
        size: 1
      }
    })

    this.replaceMarkers('executionMarkers', markers)
  }

  // Draw the IBD-style RS line: the stock's price divided by the benchmark
  // index over the server's lookback window, plotted on the price pane.
  //
  // The plotted value is an arbitrary ratio × 100, so the line gets its own
  // hidden overlay price scale — sharing the right-hand one would let a ratio
  // dictate the candles' range. The scale's margins carve out the band
  // between the price series and the volume histogram.
  //
  // The key is absent on index charts and whenever the benchmark is
  // unavailable, so an absent payload just means "no line", not an error.
  updateRsLine(rsLine) {
    // Always clear first: a ticker change on the same chart must not stack
    // lines, and switching the setting off has to remove what is drawn.
    this.clearRsLine()

    if (!this.chart || !rsLine?.series?.length || !this.getShowRsLineSetting()) return

    this.rsLineSeries = this.chart.addSeries(LineSeries, buildRsLineSeriesOptions())

    try {
      this.chart.priceScale(RS_LINE_PRICE_SCALE_ID).applyOptions({
        scaleMargins: {
          top: RS_LINE_SCALE_MARGIN_TOP,
          bottom: this.showVolumeValue ? RS_LINE_SCALE_MARGIN_BOTTOM : RS_LINE_SCALE_MARGIN_BOTTOM_NO_VOLUME
        },
        visible: false
      })
      this.chart.priceScale('right').applyOptions({ scaleMargins: this.priceScaleMargins({ rsLine: true }) })
    } catch { /* the line still draws, just wherever the default scale puts it */ }

    this.rsLineSeries.setData(rsLine.series)
    this.addRsLineMarkers(rsLine.markers)
    this.setRsLineLabel(this.rsLineLabelHtml(rsLine.benchmark))
  }

  // New-RS-high dots, drawn ON the RS line rather than on the candles — the
  // event belongs to that line, and a circle under a candle would read as
  // another entry-signal marker. The darker, larger dot is the RS high that
  // arrived while price had not yet made one.
  addRsLineMarkers(rsMarkers) {
    if (!this.rsLineSeries || !this.getShowRsLineDotsSetting()) return

    const markers = (rsMarkers || []).map(marker => {
      const leading = marker.kind === RS_LINE_LEAD_MARKER_KIND
      return {
        time: marker.time,
        position: 'inBar',
        shape: 'circle',
        color: leading ? RS_LINE_LEAD_MARKER_COLOR : RS_LINE_DOT_COLOR,
        size: leading ? RS_LINE_LEAD_MARKER_SIZE : RS_LINE_MARKER_SIZE
      }
    })

    this.replaceMarkers('rsLineMarkers', markers, this.rsLineSeries)
  }

  // "RS vs XJO" in the line's own colour, appended after the MA labels.
  rsLineLabelHtml(benchmark) {
    const name = benchmark ? ` vs ${this.escapeHtml(String(benchmark))}` : ''
    return `<span class="indicator-label" style="color: ${RS_LINE_COLOR};">[RS${name}]</span>`
  }

  // Remove the RS line, its dots and its header label. Safe to call when
  // nothing was ever drawn, and safe after the chart itself has gone.
  clearRsLine() {
    if (this.rsLineMarkers) {
      try {
        this.rsLineMarkers.detach()
      } catch {
        /* already disposed with its chart */
      } finally {
        this.rsLineMarkers = null
      }
    }

    if (this.rsLineSeries) {
      try {
        this.chart?.removeSeries(this.rsLineSeries)
        this.chart?.priceScale('right').applyOptions({ scaleMargins: this.priceScaleMargins() })
      } catch {
        /* already disposed with its chart */
      } finally {
        this.rsLineSeries = null
      }
    }

    if (this.rsLineLabel) this.setRsLineLabel(null)
  }

  // Where the candles sit on the price pane. The bottom margin makes room
  // for the volume histogram and, while one is drawn, the RS line's band.
  priceScaleMargins({ rsLine = false } = {}) {
    let bottom = this.showVolumeValue ? PRICE_SCALE_MARGIN_BOTTOM : PRICE_SCALE_MARGIN_BOTTOM_NO_VOLUME
    if (rsLine) bottom = PRICE_SCALE_MARGIN_BOTTOM_WITH_RS_LINE
    return { top: PRICE_SCALE_MARGIN_TOP, bottom }
  }

  // Get RS-line visibility from a data attribute (may be on a descendant
  // element) — same shape as getShowTrendGaugesSetting, defaulting ON.
  getShowRsLineSetting() {
    try {
      if (this.element && this.element.hasAttribute('data-lightweight-chart-show-rs-line-value')) {
        return this.showRsLineValue
      }

      if (this.element) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          const value = container.dataset.lightweightChartShowRsLineValue
          if (value !== undefined) return value === 'true'
        }
      }
    } catch (e) {
      // Controller not fully initialized
    }

    return true
  }

  // Get new-RS-high dot visibility — same shape again, defaulting ON. A
  // separate switch from the line itself: the dots are the busiest part of
  // the overlay and the first thing a member turns off.
  getShowRsLineDotsSetting() {
    try {
      if (this.element && this.element.hasAttribute('data-lightweight-chart-show-rs-line-dots-value')) {
        return this.showRsLineDotsValue
      }

      if (this.element) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          const value = container.dataset.lightweightChartShowRsLineDotsValue
          if (value !== undefined) return value === 'true'
        }
      }
    } catch (e) {
      // Controller not fully initialized
    }

    return true
  }

  // Open the chart ON the member's trades, not on the default last year.
  //
  // The holdings chart's window is derived from the position's own history, so
  // it is routinely years wide — and a position closed in 2025 then sits
  // entirely outside a "last 12 months" default, which is a chart of a trade
  // record showing none of the trades. A few bars of lead-in before the first
  // execution give the entry somewhere to sit rather than pinning it to the
  // left edge; the right edge stays at the last bar so the security's life
  // since the exit is still visible.
  //
  // Returns false when there is nothing to frame, which is every other chart
  // on the site — the caller then falls back to setDefaultVisibleRange.
  setVisibleRangeCoveringExecutions(executions, data) {
    if (!executions?.length || !data?.length) return false
    if (!this.chart || typeof this.chart.timeScale !== 'function') return false

    const earliest = executions
      .map(execution => String(execution.time))
      .sort((a, b) => a.localeCompare(b))[0]

    let index = data.findIndex(bar => String(bar.time) >= earliest)
    // An execution after the last bar (a trade recorded today, before tonight's
    // price download) frames off the end of the series — fall back to the
    // default rather than asking for an empty range.
    if (index === -1) return false

    const executionIndex = index
    // The lead-in, plus the bars scrollToRealTime() is about to steal from the
    // left edge below. Asking for `from` at the lead-in alone would put the
    // requested left edge exactly where the shift lands it 7 bars later,
    // costing the entry its lead-in.
    const from = Math.max(0, executionIndex - EXECUTION_RANGE_LEAD_BARS - CHART_RIGHT_OFFSET_BARS)
    // ...but when there is not that much history, `from` clamps at bar 0 and
    // the shift eats into the executions themselves: a security bought in its
    // first fortnight of held history would have its entry scrolled off the
    // left edge, while this function still reported the trades framed. Keep
    // the padding only when the lead-in can absorb it.
    const scrollFits = from + CHART_RIGHT_OFFSET_BARS < executionIndex

    try {
      this.chart.timeScale().setVisibleRange({
        from: new Date(`${data[from].time}T00:00:00Z`).getTime() / 1000,
        to: new Date(`${data[data.length - 1].time}T00:00:00Z`).getTime() / 1000
      })
      // The same right-hand padding setDefaultVisibleRange leaves: a range set
      // to the last bar pins that bar against the price scale, and the exit
      // marker above it is then half off the edge. scrollToRealTime() restores
      // the chart's own rightOffset.
      if (scrollFits) this.chart.timeScale().scrollToRealTime()
    } catch {
      return false
    }

    return true
  }

  // "B 500 @ 1.230", or "S 1,200 @ 4.500 (3 fills)" when the day held more
  // than one — the count is what tells the member the price is an average
  // rather than a fill they can look up in the table below.
  executionMarkerText(execution) {
    const side = execution.side === 'buy' ? 'B' : 'S'
    const quantity = Number(execution.quantity)
    const parts = [ side ]

    if (Number.isFinite(quantity)) parts.push(quantity.toLocaleString())

    // A null price means the day held no PRICED fill — a bonus issue, or a $0
    // opening balance. There is no average to print, so the marker says what
    // arrived and stops. `Number(null)` is 0, which is finite, so the null has
    // to be rejected before the conversion or the marker reads "@ 0.000".
    const cents = execution.price_cents == null ? NaN : Number(execution.price_cents)
    if (Number.isFinite(cents)) parts.push(`@ ${(cents / 100).toFixed(EXECUTION_PRICE_DECIMALS)}`)

    const text = parts.join(' ')
    return execution.count > 1 ? `${text} (${execution.count} fills)` : text
  }

  // Paint translucent red/green vertical bands behind the candles based on
  // PowerTrend active periods. Green = active, red = inactive.
  addPowerTrendBackground(periods, data) {
    if (!this.series) return

    this.removePowerTrendBackground()

    try {
      this.powerTrendBackground = new PowerTrendBackground({ periods, data })
      this.series.attachPrimitive(this.powerTrendBackground)
    } catch {
      this.powerTrendBackground = null
    }
  }

  removePowerTrendBackground() {
    if (this.powerTrendBackground && this.series) {
      try { this.series.detachPrimitive(this.powerTrendBackground) } catch { /* no-op */ }
    }
    this.powerTrendBackground = null
  }

  // Update (creating panes on first use) the Pressure/Buy Risk/TPR strips.
  // The API omits trend_gauges entirely for non-Pro users, so absence here
  // just means "don't show these panes" rather than an error.
  //
  // `data` is the same OHLCV array driving the main candle series — the
  // TrendGaugeBand primitive needs it to map each gauge row's date onto a
  // bar index on the shared time scale (trend_gauges can have gaps, e.g.
  // no 50MA yet, that the OHLCV series doesn't).
  updateTrendGauges(trendGauges, data) {
    if (!this.chart) return

    if (!trendGauges?.length || !this.getShowTrendGaugesSetting()) {
      this.removeTrendGaugePanes()
      this.clearGaugePills()
      return
    }

    this.ensureTrendGaugePanes()
    // The anchor plots a flat constant series (see TREND_GAUGE_ANCHOR_VALUE)
    // purely so its "last value" badge — which carries the gauge's name, see
    // ensureTrendGaugePanes for why that badge, not a watermark — has a
    // consistent, vertically-centered position on every ticker.
    const anchorData = data?.map(d => ({ time: d.time, value: TREND_GAUGE_ANCHOR_VALUE })) || []
    TREND_GAUGE_DEFS.forEach(({ kind }) => {
      const entry = this.trendGaugePanes?.[kind]
      if (!entry) return
      // Traffic-light the badge background by the gauge's most recent state
      // (the badge text auto-contrasts — lightweight-charts picks black or
      // white against the series color itself).
      const color = latestTrendGaugeColor(trendGauges, kind) || TREND_GAUGE_LABEL_COLOR
      entry.anchor.applyOptions({ color })
      entry.anchor.setData(anchorData)
      entry.primitive.update({ bands: buildTrendGaugeBands(trendGauges, kind), data })
    })

    // Re-pin the strip heights on every update: setting several pane
    // heights back-to-back at creation can race the library's internal
    // redistribution and leave panes collapsed to 0, and any chart resize
    // scales them (heights are stretch factors internally).
    this.applyTrendGaugePaneHeights()

    this.updateGaugePills(trendGauges)
  }

  // Pin every gauge pane to its fixed strip height. Uses setStretchFactor
  // with ratios computed from the chart's OPTIONS height, not setHeight —
  // setHeight redistributes based on the panes' current heights, which are
  // 0/stale while the library's own rAF-driven layout is still in flight
  // (pane creation, resizes, hidden tabs), so it randomly leaves strips
  // collapsed to 0 or at several times their size. Stretch ratios are
  // absolute: pane height = factor / totalFactors × available height, so
  // setting gauge factors to the strip height and the price pane to the
  // remainder yields ~30px strips regardless of when layout catches up.
  applyTrendGaugePaneHeights() {
    if (!this.chart || !this.trendGaugePanes) return
    try {
      const panes = this.chart.panes()
      const gaugeIndexes = new Set(Object.values(this.trendGaugePanes).map(e => e.paneIndex))
      // Pane budget ≈ chart height minus the time axis (~30px) and the 1px
      // separators. A few px of error only scales the strips fractionally.
      const paneBudget = Math.max(this.chart.options().height - 30 - (panes.length - 1), 200)
      const priceFactor = Math.max(paneBudget - gaugeIndexes.size * TREND_GAUGE_PANE_HEIGHT, TREND_GAUGE_PANE_HEIGHT)
      panes.forEach((pane, i) => {
        if (!pane?.setStretchFactor) return
        pane.setStretchFactor(gaugeIndexes.has(i) ? TREND_GAUGE_PANE_HEIGHT : priceFactor)
      })
    } catch { /* no-op */ }
  }

  // Color the header pills (and the header itself) by each gauge's latest
  // state. The pills live in shared/_stock_chart's card header and start
  // hidden — non-Pro users never receive a trend_gauges payload, so they
  // never appear at all. The header goes green only when ALL THREE gauges
  // are green; any non-green pill turns it red (deliberately not an
  // average — one broken gauge is enough to flag the setup).
  updateGaugePills(trendGauges) {
    if (!this.hasGaugePillTarget) return

    let allGreen = true
    this.gaugePillTargets.forEach(pill => {
      const color = latestTrendGaugeColor(trendGauges, pill.dataset.trendGaugeKind) || TREND_GAUGE_LABEL_COLOR
      pill.style.backgroundColor = color
      pill.style.color = contrastTextColor(color)
      if (color !== TREND_GAUGE_POSITIVE_COLOR) allGreen = false
    })

    if (this.hasGaugePillsTarget) this.gaugePillsTarget.classList.remove('d-none')
    if (this.hasGaugeHeaderTarget) {
      this.gaugeHeaderTarget.classList.toggle('gauges-all-green', allGreen)
      this.gaugeHeaderTarget.classList.toggle('gauges-not-green', !allGreen)
    }
  }

  clearGaugePills() {
    if (this.hasGaugePillsTarget) this.gaugePillsTarget.classList.add('d-none')
    if (this.hasGaugeHeaderTarget) {
      this.gaugeHeaderTarget.classList.remove('gauges-all-green', 'gauges-not-green')
    }
  }

  // Get setup-bar marker visibility from data attribute (may be on a
  // descendant element) — same shape as getShowTrendGaugesSetting.
  //
  // Defaults ON when no attribute is present: only views that opt into the
  // user setting render it (shared/_stock_chart), so a chart that never wires
  // it keeps drawing whatever the API sent, as before.
  getShowSetupBarsSetting() {
    try {
      if (this.element && this.element.hasAttribute('data-lightweight-chart-show-setup-bars-value')) {
        return this.showSetupBarsValue
      }

      if (this.element) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          const value = container.dataset.lightweightChartShowSetupBarsValue
          if (value !== undefined) return value === 'true'
        }
      }
    } catch (e) {
      // Controller not fully initialized
    }

    return true
  }

  // Get trend-gauge visibility setting from data attribute (may be on a
  // descendant element) — same shape as getLogScaleSetting, defaulting ON.
  getShowTrendGaugesSetting() {
    try {
      if (this.element && this.element.hasAttribute('data-lightweight-chart-show-trend-gauges-value')) {
        return this.showTrendGaugesValue
      }

      if (this.element) {
        const container = this.element.querySelector('.stock-chart-container')
        if (container) {
          const value = container.dataset.lightweightChartShowTrendGaugesValue
          if (value !== undefined) return value === 'true'
        }
      }
    } catch (e) {
      // Controller not fully initialized
    }

    return true
  }

  // Create one thin sub-pane per gauge, in order, below the price+volume
  // pane (pane 0). Each pane holds a single invisible "anchor" LineSeries
  // that exists only to host a TrendGaugeBand primitive — the colored
  // strip itself is drawn directly on canvas (one fillRect per contiguous
  // state run) rather than as chart series, since a volatile gauge can
  // flip state on most bars and creating one series per run does not scale.
  ensureTrendGaugePanes() {
    if (!this.chart || this.trendGaugePanes) return

    this.trendGaugePanes = {}
    TREND_GAUGE_DEFS.forEach(({ kind, label }, i) => {
      try {
        const paneIndex = i + 1
        const anchor = this.chart.addSeries(LineSeries, {
          lineVisible: false,
          priceLineVisible: false,
          crosshairMarkerVisible: false,
          // A canvas primitive/watermark can only draw within the pane's
          // CONTENT area — the price-scale gutter is a physically separate
          // canvas it cannot reach, so "right-aligned" text still lands a
          // full gutter-width short of the real y-axis numbers. The
          // "last value" badge (the same mechanism behind the "16.82" /
          // "627K" boxes on the main pane) draws natively *inside* that
          // gutter, so reusing it — with a formatter that returns the
          // gauge's name only for the anchor's constant plotted value,
          // blank for every other (regular tick) call — is the only way to
          // get genuine alignment with the axis labels above without every
          // gridline also showing the gauge's name.
          lastValueVisible: true,
          color: TREND_GAUGE_LABEL_COLOR,
          // Pin the pane's price range symmetrically around the constant
          // anchor value so the name badge sits vertically centered.
          autoscaleInfoProvider: () => ({
            priceRange: {
              minValue: TREND_GAUGE_ANCHOR_VALUE - 1,
              maxValue: TREND_GAUGE_ANCHOR_VALUE + 1
            }
          }),
          priceFormat: { type: 'custom', formatter: value => (value === TREND_GAUGE_ANCHOR_VALUE ? label : '') }
        }, paneIndex)

        // 'normal' (not the default 'bottom') so the strip paints OVER the
        // grid — the grid is a chart-wide option with no per-pane switch, and
        // gridlines crossing a solid state strip just read as noise.
        const primitive = new TrendGaugeBand({ zOrder: 'normal' })
        anchor.attachPrimitive(primitive)

        this.trendGaugePanes[kind] = { paneIndex, anchor, primitive }
      } catch { /* continue without this gauge's pane */ }
    })
    // Strip heights are pinned via stretch factors (not setHeight — see
    // applyTrendGaugePaneHeights) once all panes exist.
    this.applyTrendGaugePaneHeights()
  }

  removeTrendGaugePanes() {
    if (!this.trendGaugePanes) return

    Object.values(this.trendGaugePanes).forEach(({ anchor, primitive }) => {
      try { anchor.detachPrimitive(primitive) } catch { /* no-op */ }
      try { this.chart.removeSeries(anchor) } catch { /* no-op */ }
    })
    this.trendGaugePanes = null
  }

  // ---- Curated Model Book marks API --------------------------------------
  // The read side (chart_data's top-level `marks:`) and the drawing editor
  // both go through these four; the layer (utils/chart_marks_overlay.js)
  // draws price kinds on `this.series` and volume kinds on
  // `this.volumeSeries`. Mark shape:
  //   { id, kind, points: [{date, price}], label, note, author: {id, alias}, own }

  // Replace every mark on the chart.
  setMarks(marks) {
    const layer = this.ensureMarksLayer()
    if (layer) layer.setMarks(marks)
  }

  // Add (or replace, by id) one mark — the editor's commit.
  addMark(mark) {
    const layer = this.ensureMarksLayer()
    if (layer) layer.addMark(mark)
  }

  removeMark(id) {
    if (this.marksLayer) this.marksLayer.removeMark(id)
  }

  // @param ids author ids to show; null/undefined shows every author
  setVisibleAuthors(ids) {
    const layer = this.ensureMarksLayer()
    if (layer) layer.setVisibleAuthors(ids)
    // The drawing editor drops a selection whose author was just hidden.
    this.dispatch('marks-visibility-changed')
  }

  // Whether a click at pane-0 y lands in the volume strip at the bottom of
  // the price pane (the volume scale's top margin, buildVolumeScaleOptions).
  inVolumeStrip(y) {
    if (!this.chart || !this.volumeSeries || !Number.isFinite(y)) return false
    try {
      const top = this.chart.priceScale('volume').options().scaleMargins?.top
      const height = this.chart.paneSize(0)?.height
      if (!(top > 0) || !(height > 0)) return false
      return y >= height * top
    } catch {
      return false
    }
  }

  // Detach the marks layer's primitives and listeners. Must run BEFORE
  // chart.remove(): in lightweight-charts 5.2 remove() never calls a
  // primitive's detached(), so the overlay's theme:changed listener would leak.
  disposeMarksLayer() {
    if (!this.marksLayer) return
    try { this.marksLayer.dispose() } catch { /* already disposed with its chart */ }
    this.marksLayer = null
  }

  // The layer itself, for the editor's hit-testing (markOverlays.hitTest).
  get markOverlays() {
    return this.marksLayer || null
  }

  ensureMarksLayer() {
    if (this.marksLayer) return this.marksLayer
    if (!this.chart || !this.series) return null
    this.marksLayer = new ChartMarksLayer({
      chart: this.chart,
      priceSeries: this.series,
      volumeSeries: this.volumeSeries,
      container: this.chartTarget
    })
    return this.marksLayer
  }

  // The curated Model Book's opening frame (utils/model_book_framing.js): the
  // whole window when it fits at a readable bar spacing, otherwise the first
  // VISIBLE mark (the author chips) LEAD_BARS from the left edge — bar 0 on
  // load, when every author is shown — or, with every mark hidden, the
  // no-marks framing. Remembers the bars so model-book-chart's refit key can
  // re-run it with no arguments.
  frameMarksWindow(data = this.marksWindowData) {
    if (!this.chart || !Array.isArray(data) || data.length === 0) return
    this.marksWindowData = data
    const timeScale = this.chart.timeScale()
    const layer = this.marksLayer
    const marks = (layer?.marks || []).filter(mark => typeof layer.isMarkVisible !== 'function' || layer.isMarkVisible(mark.id))
    const range = initialLogicalRange({
      barCount: data.length,
      firstMarkIndex: firstMarkIndex(marks, data.map(bar => bar.time)),
      paneWidth: timeScale.width(),
      rightOffset: CHART_RIGHT_OFFSET_BARS
    })
    if (range) {
      timeScale.setVisibleLogicalRange(range)
    } else {
      timeScale.fitContent()
    }
  }

  // Set the default visible range to 1 year from the last data point.
  // Falls back to fitContent() if less than 1 year of data is available.
  setDefaultVisibleRange(data) {
    if (!this.chart || !data || data.length === 0) return

    const lastDate = data[data.length - 1].time
    const firstDate = data[0].time

    // Calculate 1 year before the last data point
    const lastParts = lastDate.split('-').map(Number)
    const oneYearAgo = `${lastParts[0] - 1}-${String(lastParts[1]).padStart(2, '0')}-${String(lastParts[2]).padStart(2, '0')}`

    if (firstDate <= oneYearAgo) {
      // More than 1 year of data — show only the last year
      // setVisibleRange requires Unix timestamps in seconds
      const fromTimestamp = new Date(oneYearAgo + 'T00:00:00Z').getTime() / 1000
      const toTimestamp = new Date(lastDate + 'T00:00:00Z').getTime() / 1000
      this.chart.timeScale().setVisibleRange({
        from: fromTimestamp,
        to: toTimestamp
      })
      // Scroll to real-time to respect rightOffset padding
      this.chart.timeScale().scrollToRealTime()
    } else {
      // Less than 1 year of data — show everything
      this.chart.timeScale().fitContent()
    }
  }

  // Update the watermark with stock information
  updateWatermark(stockInfo) {
    if (!this.chart || !stockInfo) return

    const lines = buildWatermarkLines(stockInfo)
    if (lines.length === 0) return

    // Update existing watermark or create new one
    try {
      if (this.watermark) {
        // Update existing watermark
        this.watermark.applyOptions({ lines: lines })
      } else {
        // Create new watermark
        const pane = this.chart.panes()[0]
        this.watermark = createTextWatermark(pane, {
          horzAlign: 'center',
          vertAlign: 'center',
          lines: lines
        })
      }
    } catch (err) {
      // Watermark creation/update failed - continue without it
    }
  }
}
